import React, {CSSProperties} from "react";
import generateSingleDocument from "common/document/generate_single_document";
import {generateTimeEstimateMessage} from "common/utils/generate_time_remaining_message";
import {
  getAnalysingTimeRemaining,
  getClassifyingTimeRemaining,
} from "common/utils/document_time_remaining";
import {CircularProgress} from "@material-ui/core";

interface StyleOptions {
  phaseMessage: CSSProperties;
  timeEstimateMessage: CSSProperties;
}

const requiredUploadProgressKeys = [
  "clauses_llm_time_estimate",
  "clausepart_load_count",
  "clausepart_load_total",
  "clausepart_last_edited",
  "llm_issues_count_complete",
  "llm_issues_count",
  "llm_issues_complete",
  "llm_issues",
];

const statusMessages = {
  PARSE_ERROR: "Document could not be read. An admin will contact you shortly.",
  ENCRYPTED: "Document is encrypted. Remove password protection and re-upload.",
  FILE_TOO_LONG: "Document too long to be read.",
  NO_TEXT: "Document only contains images",
  NO_SPECIAL_CONDITIONS: "Special conditions have not been detected",
  UNRECOGNISED_FORMAT: "Unrecognised file format",
};

export function hasErrorStatus(document): boolean {
  return document?.status in statusMessages;
}

function toOrdinal(number) {
  const lastNumber = number % 10;
  let ordinalStr = "th";
  if (!(number > 10 && number < 19)) {
    if (lastNumber === 1) {
      ordinalStr = "st";
    }
    if (lastNumber === 2) {
      ordinalStr = "nd";
    }
    if (lastNumber === 3) {
      ordinalStr = "rd";
    }
  }
  return `${number}${ordinalStr}`;
}

function showStatusInfoPending(uploadedDocument): boolean {
  // Need to uploadedDocument.upload_progress if the load state is
  // 0 (parsing), 7 (Analysing clauses), 6 (Analysing structure), 1 (Classifying)
  // State 9 (issues) is handled separately by showProcessingIssuesPending
  const showProgressBar = uploadedDocument.concat_load_state.some(load_state =>
    [0, 7, 6, 1, null].includes(load_state),
  );
  const hasUploadInfo = requiredUploadProgressKeys.every(
    key => key in uploadedDocument,
  );
  return showProgressBar && !hasUploadInfo;
}

function showProcessingIssuesPending(uploadedDocument): boolean {
  const hasUploadInfo = requiredUploadProgressKeys.every(
    key => key in uploadedDocument,
  );
  return (
    !hasUploadInfo &&
    uploadedDocument.concat_load_state.some(load_state => load_state === 9)
  );
}

function renderProcessingUploadedDocumentStatus(
  uploadedDocument,
  styleOptions = {} as Partial<StyleOptions>,
) {
  if (!uploadedDocument) {
    return null;
  }

  if (showStatusInfoPending(uploadedDocument)) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <CircularProgress
          style={{
            width: "2em",
            height: "2em",
          }}
        />
      </div>
    );
  }

  if (showProcessingIssuesPending(uploadedDocument)) {
    return (
      <div>
        <div
          style={{
            textAlign: "center",
            fontSize: "1.2em",
            color: "#544",
            ...styleOptions.phaseMessage,
          }}
        >
          Processing Issues
        </div>
        <div
          style={{
            marginTop: 6,
            height: 3,
            width: "100%",
            backgroundColor: "#ccc",
          }}
        >
          <div
            style={{
              height: "100%",
              backgroundColor: "#1f88e5",
              width: `${scaleProgressAmount("issuing", 0, true)}%`,
            }}
          />
        </div>
        <div
          style={{
            fontStyle: "italic",
            marginTop: 4,
            ...styleOptions.timeEstimateMessage,
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.3em",
              justifyContent: "center",
            }}
          >
            <CircularProgress size="1em" />
            Fetching Progress
          </div>
        </div>
      </div>
    );
  }

  const {
    errorMessage,
    phaseMessage,
    progressAmount,
    timeEstimate,
    amountComplete,
    queueStatusMessage,
  } = getMultiFileStatus(uploadedDocument);
  if (errorMessage) {
    return <div>{errorMessage}</div>;
  }
  if (
    amountComplete === uploadedDocument.concat_filenames?.length &&
    progressAmount > 99
  ) {
    return null;
  }
  if (phaseMessage) {
    return (
      <div>
        <div
          style={{
            textAlign: "center",
            fontSize: "1.2em",
            color: "#544",
            ...styleOptions.phaseMessage,
          }}
        >
          {amountComplete
            ? `${amountComplete}/${uploadedDocument.concat_filenames.length} Complete`
            : phaseMessage}
        </div>
        <div
          style={{
            marginTop: 6,
            height: 3,
            width: "100%",
            backgroundColor: "#ccc",
          }}
        >
          {progressAmount ? (
            <div
              style={{
                height: "100%",
                backgroundColor: "#1f88e5",
                width: `${progressAmount}%`,
              }}
            />
          ) : (
            <></>
          )}
        </div>
        <div
          style={{
            fontStyle: "italic",
            marginTop: 4,
            ...styleOptions.timeEstimateMessage,
          }}
        >
          {queueStatusMessage
            ? queueStatusMessage
            : generateTimeEstimateMessage(timeEstimate)}
        </div>
      </div>
    );
  }
}

function getMultiFileStatus(uploadedDocument) {
  if ((uploadedDocument?.concat_filenames?.length ?? 1) === 1) {
    const status = getDocumentStatus(
      generateSingleDocument(uploadedDocument, 0),
    );
    return {...status, amountComplete: null};
  }

  let allErrorMessage: string | null = null;
  let bestPhaseMessage: string | null = null;
  let bestQueueMessage: string | null = null;
  let bestProgressAmount: number | null = null;
  let totalProgressAmount = 0;
  let totalReadAmount = 0;
  let totalTimeEstimate = 0;
  let amountComplete = 0;

  uploadedDocument.concat_filenames.find((filename, index) => {
    const indexDocument = generateSingleDocument(uploadedDocument, index);
    const {
      errorMessage,
      phaseMessage,
      progressAmount,
      timeEstimate,
      queueStatusMessage,
      awaitingIssues,
    } = getDocumentStatus(indexDocument, index);
    if (errorMessage) {
      allErrorMessage = errorMessage;
      return true;
    } else {
      if (awaitingIssues) {
        amountComplete += 1;
      }
      if (bestProgressAmount === null || progressAmount > bestProgressAmount) {
        bestPhaseMessage = phaseMessage;
        bestProgressAmount = progressAmount;
        bestQueueMessage = queueStatusMessage;
      }
      const readAmount = indexDocument.text_length ?? 0;
      totalProgressAmount =
        (totalProgressAmount * totalReadAmount + progressAmount * readAmount) /
        (totalReadAmount + readAmount);
      totalReadAmount += readAmount;
      totalTimeEstimate = Math.max(totalTimeEstimate, timeEstimate ?? 0);
    }
    return false;
  });
  return {
    errorMessage: allErrorMessage,
    phaseMessage: bestPhaseMessage,
    progressAmount: totalProgressAmount,
    timeEstimate: totalTimeEstimate,
    queueStatusMessage: bestQueueMessage,
    amountComplete,
  };
}

interface DocumentStatus {
  errorMessage: string;
  phaseMessage: string;
  queueStatusMessage: string;
  progressAmount: number;
  timeEstimate: number;
}

function scaleProgressAmount(
  phase: "parsing" | "classifying" | "issuing",
  amount: number,
  useLlms: boolean,
): number {
  const llmScale = {
    parsing: {
      initialAmount: 0,
      increase: 30,
    },
    classifying: {
      initialAmount: 30,
      increase: 50,
    },
    issuing: {
      initialAmount: 80,
      increase: 20,
    },
  };

  const classicScale = {
    parsing: {
      initialAmount: 0,
      increase: 50,
    },
    classifying: {
      initialAmount: 50,
      increase: 49,
    },
    issuing: {
      initialAmount: 99,
      increase: 1,
    },
  };

  const scale = useLlms ? llmScale : classicScale;

  return scale[phase].initialAmount + (amount / 100) * scale[phase].increase;
}

function getProcessingIssuesStatus(uploadedDocument, useLlms: boolean) {
  let phaseMessage = "Finalising";
  let progressAmount = scaleProgressAmount("issuing", 0, useLlms);
  let timeEstimate: number | null = null;

  if (useLlms) {
    const {
      llm_issues_count,
      llm_issues_count_complete,
      llm_issues,
      llm_issues_complete,
      llm_issue_task_counts,
    } = uploadedDocument;

    const baseAmount = llm_issues_count_complete / llm_issues_count;
    let percentComplete = baseAmount * 100;

    if (llm_issue_task_counts) {
      const incompleteIds = (llm_issues ?? []).filter(
        id => !(llm_issues_complete ?? []).includes(id),
      );
      const completeTasks = incompleteIds.reduce(
        (sum, id) => sum + (llm_issue_task_counts[id]?.completed_count ?? 0),
        0,
      );
      const totalTasks = incompleteIds.reduce(
        (sum, id) => sum + (llm_issue_task_counts[id]?.total_count ?? 0),
        0,
      );
      if (totalTasks > 0) {
        const incompleteAmount = completeTasks / totalTasks;
        percentComplete =
          (baseAmount + incompleteAmount * (1 - baseAmount)) * 100;
      }
    }

    timeEstimate = getLlmIssuesProcessingTime(uploadedDocument);
    phaseMessage = `Processing Issues ${Math.round(percentComplete)}%`;
    progressAmount = scaleProgressAmount("issuing", percentComplete, true);
  }

  return {phaseMessage, progressAmount, timeEstimate};
}

function getDocumentStatus(uploadedDocument, index = 0): DocumentStatus {
  const useLlms = (uploadedDocument.llm_issues_count ?? 0) > 0;

  const {
    load_state: loadState,
    parse_state: parseState,
    status,
    analyser_progress: analyserProgress,
    // should store a new value to say when analysis started
    creation_date: creationDate,
    text_length: textLength,
  } = uploadedDocument;
  let errorMessage;
  let phaseMessage;
  let progressAmount = 0;
  let timeEstimate: number | null = null;
  let queueStatusMessage;
  let awaitingIssues = false;
  if (Object.keys(statusMessages).includes(status)) {
    errorMessage = statusMessages[status];
  } else if (uploadedDocument.id === -1) {
    errorMessage = uploadedDocument.upload_failed ? "Upload failed" : " ";
  } else if (!uploadedDocument.file_extension) {
    errorMessage = "Unknown file extension";
  } else if (status === "EMPTY_DOCUMENT" || status === "FILE_EMPTY") {
    errorMessage = "Document Empty";
  } else if (loadState === 9) {
    awaitingIssues = true;
    ({phaseMessage, progressAmount, timeEstimate} = getProcessingIssuesStatus(
      uploadedDocument,
      useLlms,
    ));
  } else if (loadState === 4) {
    phaseMessage = "Queued";
    progressAmount = 1;
    if (uploadedDocument.queue_order && index === 0) {
      phaseMessage = `Queued (${toOrdinal(uploadedDocument.queue_order)})`;
    }
  } else if (loadState === 3) {
    progressAmount = 100;
  } else if (loadState === 2) {
    progressAmount = 100;
    errorMessage = "Document is being reviewed and will be unlocked shortly.";
  } else if (loadState === 5) {
    errorMessage = "Document could not be classified.";
  } else if (loadState === 0) {
    if (parseState) {
      if (parseState >= 200 && parseState <= 299) {
        phaseMessage = "Reading Document";
      } else if (parseState >= 300 && parseState <= 399) {
        phaseMessage = "OCR images";
        progressAmount = scaleProgressAmount("parsing", 10, useLlms);
      } else if (parseState >= 400 && parseState <= 499) {
        phaseMessage = "Analysing structure";
        progressAmount = scaleProgressAmount("parsing", 14, useLlms);
      } else if (parseState >= 500 && parseState <= 599) {
        phaseMessage = "Analysing clauses";
        progressAmount = scaleProgressAmount("parsing", 20, useLlms);
      } else {
        // phaseMessage = "ScanningX"+parseState;
        phaseMessage = "Scanning";
      }
      timeEstimate = getTimeEstimateWithLlms(
        getAnalysingTimeRemaining(textLength, creationDate),
        uploadedDocument,
        useLlms,
      );
    } else {
      // phaseMessage = "Scanning0_"+parseState;
      phaseMessage = "Scanning";
    }
  } else if (loadState === 6) {
    // phaseMessage = "Scanning6_"+parseState;
    phaseMessage = "Analysing structure";
    progressAmount = scaleProgressAmount("parsing", 14, useLlms);
    timeEstimate = getTimeEstimateWithLlms(
      getAnalysingTimeRemaining(textLength, creationDate),
      uploadedDocument,
      useLlms,
    );
  } else if (loadState === 7) {
    phaseMessage = "Analysing clauses";
    progressAmount = scaleProgressAmount("parsing", 16, useLlms);
    queueStatusMessage = `${
      uploadedDocument.queue_order && index === 0
        ? `(${toOrdinal(uploadedDocument.queue_order)} in queue)`
        : ""
    }`;
    timeEstimate = getTimeEstimateWithLlms(
      getAnalysingTimeRemaining(textLength, creationDate),
      uploadedDocument,
      useLlms,
    );

    if (analyserProgress && analyserProgress > 0) {
      progressAmount = scaleProgressAmount(
        "parsing",
        24 + Math.floor(Math.min(analyserProgress, 1) * 100 - 24),
        true,
      );
    } else {
      progressAmount = scaleProgressAmount("parsing", 24, useLlms);
    }
  } else if (loadState === 8) {
    phaseMessage = "Storing clauses";
    progressAmount = scaleProgressAmount("parsing", 98, useLlms);
  } else if (loadState === 1) {
    phaseMessage = "Classifying";
    progressAmount = scaleProgressAmount("classifying", 0, useLlms);
    if (uploadedDocument.classification_start) {
      const timeRemaining = getClassifyingTimeRemaining(
        uploadedDocument.clausepart_last_edited,
        uploadedDocument.classification_start,
        uploadedDocument.clausepart_load_count,
        uploadedDocument.clausepart_load_total,
        uploadedDocument.clauses_llm_time_estimate,
      );
      progressAmount = scaleProgressAmount(
        "classifying",
        getClassifyingAmount(
          uploadedDocument.clausepart_load_count,
          uploadedDocument.clausepart_load_total,
        ),
        useLlms,
      );

      timeEstimate = getTimeEstimateWithLlms(
        timeRemaining,
        uploadedDocument,
        useLlms,
      );
    }
  }

  return {
    errorMessage,
    phaseMessage,
    progressAmount,
    timeEstimate,
    queueStatusMessage,
    awaitingIssues,
  };
}

function getClassifyingAmount(
  clausepartLoadCount: number,
  clausepartLoadTotal: number,
): number {
  return clausepartLoadCount === 0
    ? 0
    : clausepartLoadCount === clausepartLoadTotal
    ? 100
    : Math.round((clausepartLoadCount * 100 * 0.99) / clausepartLoadTotal);
}

function getLlmIssuesProcessingTime(uploadedDocument) {
  return uploadedDocument.llm_issues_processing_time;
}

function getTimeEstimateWithLlms(timeRemaining, uploadedDocument, useLlms) {
  return useLlms
    ? timeRemaining + getLlmIssuesProcessingTime(uploadedDocument)
    : timeRemaining;
}

export default renderProcessingUploadedDocumentStatus;
